/**
 * Google Analytics 4 (gtag.js) helpers for the Fitting SPA.
 *
 * The gtag script tag + initial `gtag('config', …)` call lives in
 * apps/fitting/index.html — that fires one page_view on the initial
 * hard load. SPA navigations after that need to call
 * trackPageView() manually because React Router doesn't reload the
 * document. AnalyticsRouteTracker.tsx does this on every
 * useLocation() change.
 *
 * Measurement ID lives in index.html only — keep it there so the
 * <script> tag and the config call stay co-located. If we ever need
 * the ID in JS land, expose it via import.meta.env.
 */

declare global {
  interface Window {
    /** Injected by the gtag.js loader in index.html. Always defined
     *  on web; in Capacitor (where there's no remote script load)
     *  it may be undefined for the first few hundred ms. Callers
     *  must null-check. */
    gtag?: (...args: unknown[]) => void;
  }
}

/** Fire a GA4 page_view event. Safe to call before gtag.js has loaded —
 *  the loader inline script in index.html defines `window.gtag` as a
 *  queue-pusher synchronously, so calls before the async script
 *  resolves still land in `dataLayer` and get flushed once the
 *  remote script is ready. */
export function trackPageView(path: string, title?: string): void {
  if (typeof window === 'undefined') return;
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
}

export {};
